import { useCallback, useMemo, useState } from "react";
import {
  ArrowRight,
  Ban,
  BookmarkCheck,
  Check,
  Clock,
  History,
  Loader2,
  Lock,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { ApiClientError } from "@/api/client";
import { usePatronRenewals, useRenewPatronLoans } from "@/api/hooks";
import type {
  LoanRenewalOutcome,
  PatronRenewalEntry,
  RenewalBlocker,
  Loan,
  RenewalBlockerCode,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  BLOCKER_PRESENTATION,
  accountGate,
  formatShortDate,
  isSelectable,
  rowBlocker,
  type AccountGate,
  type BlockerTone,
} from "../renewalLedger";

const TONE_CLASSES: Record<BlockerTone, string> = {
  muted: "bg-muted text-muted-foreground",
  info: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300",
  danger: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

const BLOCKER_ICONS: Record<RenewalBlockerCode, LucideIcon> = {
  RENEWAL_LIMIT_REACHED: History,
  RESERVATION_BLOCKING: BookmarkCheck,
  TOO_EARLY_TO_RENEW: Clock,
  RENEWALS_NOT_ALLOWED: Ban,
  LOAN_NOT_ACTIVE: Ban,
  PATRON_BLOCKED: Lock,
  PATRON_SUSPENDED: Lock,
  PATRON_EXPIRED: Lock,
};

/**
 * Renewal desk for one patron: every active loan with what a renewal would do,
 * or why it cannot happen, and a selection renewed in one action.
 */
export function RenewalLedger({ patronBarcode }: { patronBarcode: string }) {
  const { data, isLoading, error, refetch } = usePatronRenewals(patronBarcode);
  const renewLoans = useRenewPatronLoans();

  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [overrideActive, setOverrideActive] = useState(false);
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, LoanRenewalOutcome>>(new Map());
  const [summary, setSummary] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  const gate: AccountGate = data ? accountGate(data) : { kind: "open" };
  const overrideInEffect = gate.kind === "overridable" && overrideActive;

  // A loan renewed in this session shows its confirmation rather than its new blockers
  const isRenewedHere = useCallback(
    (loanId: string) => outcomes.get(loanId)?.success === true,
    [outcomes]
  );

  const selectableIds = useMemo(
    () =>
      (data?.loans ?? [])
        .filter((entry) => isSelectable(entry, overrideInEffect) && !isRenewedHere(entry.loan.id))
        .map((entry) => entry.loan.id),
    [data, overrideInEffect, isRenewedHere]
  );
  const selectedIds = selectableIds.filter((id) => selected.has(id));
  const allSelected = selectableIds.length > 0 && selectedIds.length === selectableIds.length;

  const toggle = useCallback((loanId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(loanId)) next.delete(loanId);
      else next.add(loanId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(allSelected ? new Set() : new Set(selectableIds));
  }, [allSelected, selectableIds]);

  const renew = useCallback(
    async (loanIds: string[]) => {
      if (loanIds.length === 0) return;
      setSummary(null);

      try {
        const response = await renewLoans.mutateAsync({
          patronBarcode,
          loanIds,
          overridePatronStatus: overrideInEffect,
        });
        setOutcomes((current) => {
          const next = new Map(current);
          for (const outcome of response.results) next.set(outcome.loanId, outcome);
          return next;
        });
        setSelected((current) => {
          const next = new Set(current);
          for (const id of loanIds) next.delete(id);
          return next;
        });
        setSummary(summarize(response.renewedCount, response.refusedCount));
      } catch (err) {
        setSummary({
          tone: "error",
          text: err instanceof Error ? err.message : "La prolongation a échoué",
        });
      }
    },
    [patronBarcode, overrideInEffect, renewLoans]
  );

  if (isLoading) return <LedgerSkeleton />;

  if (error || !data) {
    const notFound = error instanceof ApiClientError && error.status === 404;
    return (
      <LedgerShell>
        <div className="py-8 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {notFound
              ? "Aucun lecteur ne correspond à cette carte."
              : "Impossible de charger les prêts du lecteur."}
          </p>
          {!notFound && (
            <Button variant="outline" size="sm" className="mt-4" onClick={() => void refetch()}>
              Réessayer
            </Button>
          )}
        </div>
      </LedgerShell>
    );
  }

  const isPending = renewLoans.isPending;
  const pendingIds = new Set(renewLoans.isPending ? renewLoans.variables.loanIds : []);

  return (
    <LedgerShell
      count={data.loans.length}
      hint={
        data.loans.length > 0
          ? `${String(selectableIds.length)} prolongeable${selectableIds.length > 1 ? "s" : ""}`
          : null
      }
    >
      {gate.kind !== "open" && (
        <AccountBanner
          gate={gate}
          overrideActive={overrideActive}
          onOverrideChange={(value) => {
            setOverrideActive(value);
            setSelected(new Set());
          }}
        />
      )}

      {summary && (
        <p
          role={summary.tone === "error" ? "alert" : "status"}
          className={cn(
            "animate-fade-in mb-4 rounded-lg px-4 py-3 text-sm",
            summary.tone === "success"
              ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
              : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300"
          )}
        >
          {summary.text}
        </p>
      )}

      {data.loans.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
          Aucun prêt en cours pour ce lecteur.
        </p>
      ) : (
        <>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
            {data.loans.map((entry) => (
              <LedgerRow
                key={entry.loan.id}
                entry={entry}
                outcome={outcomes.get(entry.loan.id)}
                selectable={selectableIds.includes(entry.loan.id)}
                checked={selected.has(entry.loan.id)}
                pending={pendingIds.has(entry.loan.id)}
                disabled={isPending}
                blocker={rowBlocker(entry, overrideInEffect)}
                onToggle={toggle}
                onRenew={(loanId) => void renew([loanId])}
              />
            ))}
          </ul>

          {selectableIds.length > 0 && (
            <div className="sticky bottom-4 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur dark:border-gray-700 dark:bg-gray-800/95">
              <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  className="accent-primary h-4 w-4"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={isPending}
                />
                Tout sélectionner
              </label>
              <Button
                onClick={() => void renew(selectedIds)}
                disabled={selectedIds.length === 0 || isPending}
                className={
                  overrideInEffect ? "bg-amber-600 text-white hover:bg-amber-700" : undefined
                }
              >
                {isPending ? <Loader2 className="animate-spin" aria-hidden /> : null}
                {isPending
                  ? "Prolongation…"
                  : `${overrideInEffect ? "Prolonger avec dérogation" : "Prolonger la sélection"} (${String(selectedIds.length)})`}
              </Button>
            </div>
          )}
        </>
      )}
    </LedgerShell>
  );
}

function summarize(renewed: number, refused: number): { tone: "success" | "error"; text: string } {
  const parts: string[] = [];
  if (renewed > 0)
    parts.push(
      `${String(renewed)} prêt${renewed > 1 ? "s" : ""} prolongé${renewed > 1 ? "s" : ""}`
    );
  if (refused > 0) parts.push(`${String(refused)} refusé${refused > 1 ? "s" : ""}`);
  return { tone: renewed > 0 ? "success" : "error", text: parts.join(" · ") };
}

function LedgerShell({
  count,
  hint,
  children,
}: {
  count?: number;
  hint?: string | null;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-labelledby="renewal-ledger-title"
      className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <header className="mb-4 flex items-baseline justify-between gap-4">
        <h2 id="renewal-ledger-title" className="text-lg font-medium text-gray-900 dark:text-white">
          Prêts en cours{count !== undefined ? ` (${String(count)})` : ""}
        </h2>
        {hint && <span className="text-sm text-gray-500 dark:text-gray-400">{hint}</span>}
      </header>
      {children}
    </section>
  );
}

function LedgerSkeleton() {
  return (
    <LedgerShell>
      <div aria-busy="true" aria-label="Chargement des prêts" className="space-y-4">
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex animate-pulse items-center gap-4 py-2">
            <div className="h-4 w-4 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-1/4 rounded bg-gray-100 dark:bg-gray-700/60" />
            </div>
            <div className="h-6 w-28 rounded-full bg-gray-100 dark:bg-gray-700/60" />
          </div>
        ))}
      </div>
    </LedgerShell>
  );
}

function AccountBanner({
  gate,
  overrideActive,
  onOverrideChange,
}: {
  gate: Exclude<AccountGate, { kind: "open" }>;
  overrideActive: boolean;
  onOverrideChange: (value: boolean) => void;
}) {
  const overridable = gate.kind === "overridable";

  return (
    <div
      className={cn(
        "mb-4 rounded-lg border px-4 py-3",
        overridable && overrideActive
          ? "border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-900/15"
          : "border-red-200 bg-red-50 dark:border-red-800/60 dark:bg-red-900/15"
      )}
    >
      <div className="flex items-start gap-3">
        <ShieldAlert
          className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
          aria-hidden
        />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-medium text-gray-900 dark:text-white">
            {gate.title} ·{" "}
            {overridable && overrideActive
              ? "dérogation administrateur active"
              : "prolongations impossibles"}
          </p>
          {gate.detail && <p className="mt-0.5 text-gray-600 dark:text-gray-300">{gate.detail}</p>}
        </div>
      </div>

      {overridable && (
        <div className="mt-3 flex items-center justify-between gap-4 border-t border-black/5 pt-3 dark:border-white/10">
          <div className="text-sm">
            <p id="renewal-override-label" className="font-medium text-gray-900 dark:text-white">
              Déroger en tant qu&apos;administrateur
            </p>
            <p id="renewal-override-help" className="text-gray-600 dark:text-gray-300">
              Les autres règles s&apos;appliquent toujours ; la dérogation est journalisée.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={overrideActive}
            aria-labelledby="renewal-override-label"
            aria-describedby="renewal-override-help"
            onClick={() => {
              onOverrideChange(!overrideActive);
            }}
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
              overrideActive ? "bg-amber-500" : "bg-gray-300 dark:bg-gray-600"
            )}
          >
            <span
              className={cn(
                "inline-block h-5 w-5 rounded-full bg-white shadow transition-transform",
                overrideActive ? "translate-x-5" : "translate-x-0.5"
              )}
            />
          </button>
        </div>
      )}
    </div>
  );
}

function LedgerRow({
  entry,
  outcome,
  selectable,
  checked,
  pending,
  disabled,
  blocker,
  onToggle,
  onRenew,
}: {
  entry: PatronRenewalEntry;
  outcome: LoanRenewalOutcome | undefined;
  selectable: boolean;
  checked: boolean;
  pending: boolean;
  disabled: boolean;
  blocker: RenewalBlocker | null;
  onToggle: (loanId: string) => void;
  onRenew: (loanId: string) => void;
}) {
  const { loan, eligibility } = entry;
  const checkboxId = `renew-${loan.id}`;
  const renewed = outcome?.success === true ? outcome : null;
  const refused = outcome?.success === false ? outcome : null;

  return (
    <li
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-2 py-3 transition-colors",
        renewed && "animate-fade-in",
        !selectable && !renewed && "opacity-80"
      )}
    >
      <div className="flex w-5 shrink-0 justify-center">
        {selectable ? (
          <input
            id={checkboxId}
            type="checkbox"
            className="accent-primary h-4 w-4"
            checked={checked}
            disabled={disabled}
            onChange={() => {
              onToggle(loan.id);
            }}
          />
        ) : renewed ? (
          <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
        ) : (
          <Lock className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1 basis-48">
        <label
          htmlFor={selectable ? checkboxId : undefined}
          className={cn(
            "block truncate text-sm font-medium text-gray-900 dark:text-white",
            selectable && "cursor-pointer"
          )}
        >
          {loan.itemTitle}
        </label>
        <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-mono">{loan.itemBarcode}</span>
          <RenewalPips
            used={(renewed?.loan ?? loan).renewalCount}
            max={(renewed?.loan ?? loan).maxRenewals}
          />
        </div>
        {refused && (
          <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-400">
            {refused.error.message}
          </p>
        )}
      </div>

      <div className="flex items-center gap-3">
        {renewed ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-300">
            Prolongé · retour le{" "}
            <span className="font-medium">{formatShortDate(renewed.newDueDate)}</span>
          </p>
        ) : selectable ? (
          <>
            <DueShift
              dueDate={loan.dueDate}
              projectedDueDate={eligibility.projectedDueDate}
              renewalDays={eligibility.renewalDays}
              overdueDays={loan.isOverdue ? loan.daysOverdue : 0}
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() => {
                onRenew(loan.id);
              }}
              aria-label={`Prolonger « ${loan.itemTitle} »`}
            >
              {pending ? <Loader2 className="animate-spin" aria-hidden /> : "Prolonger"}
            </Button>
          </>
        ) : blocker ? (
          <BlockerNote blocker={blocker} loan={loan} />
        ) : null}
      </div>
    </li>
  );
}

/** Current due date sliding to the projected one: what the renewal will change. */
function DueShift({
  dueDate,
  projectedDueDate,
  renewalDays,
  overdueDays,
}: {
  dueDate: string;
  projectedDueDate: string;
  renewalDays: number;
  overdueDays: number;
}) {
  return (
    <p
      className="flex items-center gap-2 text-sm tabular-nums"
      aria-label={`Retour prévu le ${formatShortDate(dueDate)}, reporté au ${formatShortDate(projectedDueDate)}`}
    >
      <span
        className={cn(
          "text-gray-500 line-through decoration-gray-300 dark:text-gray-400 dark:decoration-gray-600",
          overdueDays > 0 && "text-red-600 decoration-red-300 dark:text-red-400"
        )}
        title={overdueDays > 0 ? `${String(overdueDays)} j de retard` : undefined}
      >
        {formatShortDate(dueDate)}
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-gray-400" aria-hidden />
      <span className="font-medium text-gray-900 dark:text-white">
        {formatShortDate(projectedDueDate)}
      </span>
      <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[11px] font-medium">
        +{renewalDays} j
      </span>
    </p>
  );
}

function BlockerNote({ blocker, loan }: { blocker: RenewalBlocker; loan: Loan }) {
  const presentation = BLOCKER_PRESENTATION[blocker.code];
  const Icon = BLOCKER_ICONS[blocker.code];
  // A loan whose policy allows no renewal at all was never "already renewed"
  const label =
    blocker.code === "RENEWAL_LIMIT_REACHED" && loan.renewalCount === 0
      ? BLOCKER_PRESENTATION.RENEWALS_NOT_ALLOWED.label
      : presentation.label;
  const detail = blockerDetail(blocker);

  return (
    <div className="flex max-w-xs flex-col items-end gap-1 text-right">
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
          TONE_CLASSES[presentation.tone]
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </span>
      <span className="text-xs text-gray-500 dark:text-gray-400">
        {detail ? `${detail} · retour le ` : "Retour le "}
        {formatShortDate(loan.dueDate)}
      </span>
    </div>
  );
}

/**
 * Detail line under the chip, worded so it complements the chip instead of repeating it.
 * Account blockers return null: the account banner already explains them.
 */
function blockerDetail(blocker: RenewalBlocker): string | null {
  switch (blocker.code) {
    case "RENEWAL_LIMIT_REACHED":
      return "Limite atteinte";
    case "RESERVATION_BLOCKING":
      return "Un autre lecteur l'attend";
    case "TOO_EARLY_TO_RENEW":
      return blocker.availableFrom
        ? `Possible dès le ${formatShortDate(blocker.availableFrom)}`
        : blocker.message;
    case "PATRON_BLOCKED":
    case "PATRON_SUSPENDED":
    case "PATRON_EXPIRED":
      return null;
    default:
      return blocker.message;
  }
}

/** One pip per allowed renewal, filled for each renewal already used. */
function RenewalPips({ used, max }: { used: number; max: number }) {
  if (max <= 0) return null;

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex gap-0.5" aria-hidden>
        {Array.from({ length: max }, (_, index) => (
          <span
            key={index}
            className={cn(
              "h-1.5 w-3 rounded-full",
              index < used ? "bg-gray-500 dark:bg-gray-300" : "bg-gray-200 dark:bg-gray-600"
            )}
          />
        ))}
      </span>
      <span className="sr-only">
        {used} prolongation{used > 1 ? "s" : ""} utilisée{used > 1 ? "s" : ""} sur {max}
      </span>
      <span aria-hidden>
        {used}/{max}
      </span>
    </span>
  );
}
